// Pure, dependency-free portal UX helpers (portal-phones-ux, 2026-11-04 audit).
//
// Plain ESM JS + a co-located portalUx.d.ts (allowJs is off) so the Vite bundle and the Node
// test runner share one source: api/test/portal_ux_ui.test.mjs imports this file directly.
// No React, no window, no localStorage in here.

// ---- roles + client-side route gating ------------------------------------------------------
// Defence in depth only: the server is the enforcement point. This keeps a department admin or
// end user from landing on a system page that would only render 403s.

export function isSysAdmin(me) {
  if (!me) return false;
  if (me.role === 'super_admin' || me.role === 'reseller') return true;
  return !!me.app_role && /^system\b/i.test(String(me.app_role));
}

export function isAdmin(me) {
  if (!me) return false;
  if (isSysAdmin(me)) return true;
  return !!me.app_role && /admin/i.test(String(me.app_role));
}

// Pages backed by the server's SYSTEM_ONLY_PREFIXES (/api/backup, /api/advanced, /api/settings,
// /api/outbound-rules, /api/integrations, /api/service-principals, /api/system) plus the
// top-level superOnly entries in components/Layout.tsx. Deliberately NOT gated: System sub-menu
// pages whose API is per-department (alerts, health, pickup groups, ...) -- a department admin
// reaching those by link still gets their own department's data.
export const SYSTEM_ROUTE_PREFIXES = [
  '/voice-chat', '/trunks', '/outbound-rules', '/outbound-rules-v2', '/boss-secretary', '/backup',
  '/integrations', '/settings', '/wakeup-calls', '/room-status', '/ldap-sync', '/advanced',
  '/system', '/service-principals', '/import', '/reseller',
];

// Admin-console pages an end user may open (everything else in the console is admin-only).
export const USER_ROUTE_PREFIXES = ['/webphone', '/apps', '/feature-codes'];

function underPrefix(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

// 'ok' | 'need-admin' | 'need-system' for an admin-console pathname.
export function routeAccess(pathname, me) {
  const p = String(pathname || '/').replace(/\/+$/, '') || '/';
  if (SYSTEM_ROUTE_PREFIXES.some(pre => underPrefix(p, pre))) return isSysAdmin(me) ? 'ok' : 'need-system';
  if (p === '/' || USER_ROUTE_PREFIXES.some(pre => underPrefix(p, pre))) return 'ok';
  return isAdmin(me) ? 'ok' : 'need-admin';
}

// ---- errors ---------------------------------------------------------------------------------

// lib/api req() throws Error(`${status}: ${body}`); fetch helpers throw Error(body). Turn either
// into { status, message } a person can read. Returns null for the auth-redirect error (the
// app is already navigating to /login -- no toast).
export function describeError(err) {
  if (!err) return { status: null, message: 'Something went wrong.' };
  if (err.name === 'AuthRejectedError') return null;
  const raw = String(err.message != null ? err.message : err);
  let status = null;
  let body = raw;
  const m = raw.match(/^(\d{3}):\s*([\s\S]*)$/);
  if (m) { status = Number(m[1]); body = m[2]; }
  let detail = body.trim();
  if (detail.startsWith('{')) {
    try {
      const j = JSON.parse(detail);
      detail = String(j.error || j.message || j.reason || '').trim();
    } catch (_) { /* keep text */ }
  }
  if (/^<!doctype|^<html/i.test(detail)) detail = '';
  if (detail.length > 240) detail = detail.slice(0, 240) + '...';
  if (status === 403) return { status, message: detail ? `Not allowed: ${detail}` : 'Not allowed: your account does not have access to this.' };
  if (status === 404) return { status, message: detail ? `Not found: ${detail}` : 'Not found.' };
  if (status && status >= 500) return { status, message: detail ? `Server error: ${detail}` : `Server error (${status}).` };
  if (/failed to fetch|networkerror|load failed/i.test(detail)) return { status, message: 'Could not reach the server. Check your connection and try again.' };
  return { status, message: detail || (status ? `Request failed (${status}).` : 'Request failed.') };
}

// ---- URLs -----------------------------------------------------------------------------------

// Append query params with the right separator (the audit export built `...csv?a=b?token=`).
export function withQuery(base, params) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  if (!qs) return base;
  return base + (base.includes('?') ? '&' : '?') + qs;
}

// Filename from a Content-Disposition header, else the fallback.
export function filenameFromDisposition(header, fallback) {
  const h = String(header || '');
  const star = h.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star) { try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch (_) { /* fall through */ } }
  const plain = h.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : fallback;
}

// ---- action results -------------------------------------------------------------------------

// Toast for the CRM "Test" button. The server's current check only confirms settings exist; only
// claim a verified connection when the server says it actually probed the CRM.
export function crmTestToast(r) {
  if (!r || r.ok === false) return { kind: 'error', message: (r && r.message) || 'CRM test failed.' };
  if (r.verified === true || r.probed === true) return { kind: 'success', message: r.message || 'CRM connection verified.' };
  return {
    kind: 'info',
    message: 'Saved CRM settings were found, but no live connection check was performed' + (r.message ? ` (server said: ${r.message})` : '') + '.',
  };
}

// Toast for Phones page Reboot / Reprovision results.
export function phoneActionToast(action, r, label) {
  const who = label ? ` ${label}` : '';
  if (action === 'reboot') {
    if (r && r.ok) return { kind: 'success', message: `Reboot requested for${who || ' the phone'}${r.request_id ? ` (request ${r.request_id}; retried automatically if the phone does not respond)` : ''}.` };
    return { kind: 'error', message: `Reboot failed for${who || ' the phone'}${r && (r.error || r.reason) ? `: ${r.error || r.reason}` : '.'}` };
  }
  if (r && r.ok) return { kind: 'success', message: `${(label ? label + ': ' : '')}${r.message || 'Reprovision queued.'}` };
  return { kind: 'error', message: `${(label ? label + ': ' : '')}${(r && (r.message || r.error)) || 'Reprovision failed.'}` };
}

// ---- department scope -----------------------------------------------------------------------

// What a per-department page (Phones, System) is actually showing. "All departments" is not a
// real scope on those pages: they fall back to the admin's home department -- say so.
export function describeScope({ activeTenant, homeDomain, depts }) {
  const name = (uuid) => {
    const d = (depts || []).find(x => x && x.domain_uuid === uuid);
    return d ? (d.domain_description || d.domain_name || uuid) : (uuid || '');
  };
  const all = !activeTenant || activeTenant === 'all';
  if (!all) return { domainUuid: activeTenant, label: name(activeTenant), fallback: false };
  if (homeDomain) return { domainUuid: homeDomain, label: name(homeDomain), fallback: true };
  return { domainUuid: null, label: '', fallback: true };
}

// ---- recording ------------------------------------------------------------------------------

// Users-list Recording cell. user = { record_calls, record_external_only } | undefined.
export function recordingCell(deptEnabled, user) {
  const on = !!(user && user.record_calls);
  if (!on) return { text: 'Off', tone: 'muted', title: 'Call recording is off for this user.' };
  const scope = user.record_external_only ? 'External' : 'All calls';
  if (!deptEnabled) {
    return { text: `${scope} (inactive)`, tone: 'warning', title: 'Turned on for this user, but the department recording switch (Recordings -> Settings) is off, so nothing is recorded.' };
  }
  return { text: scope, tone: 'success', title: user.record_external_only ? 'Recording external calls only.' : 'Recording all calls.' };
}
